import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import MagneticButton from "@/components/MagneticButton";

const EASE = [0.22, 1, 0.36, 1];

function HeroLine({ children, delay, active, className = "" }) {
    return (
        <span className={`block overflow-hidden ${className}`}>
            <motion.span
                className="block will-change-transform"
                initial={{ y: "115%" }}
                animate={active ? { y: 0 } : {}}
                transition={{ duration: 1.1, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero({ introDone }) {
    const navigate = useNavigate();
    const d = (n) => (introDone ? 0.15 + n * 0.12 : 0);

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative h-[100svh] overflow-clip"
        >
            <motion.div
                className="absolute inset-0"
                initial={{ scale: 1.15 }}
                animate={introDone ? { scale: 1 } : {}}
                transition={{ duration: 2.8, ease: EASE }}
            >
                <motion.img
                    src="https://customer-assets-agu9un31.emergentagent.net/job_abi-deploy-check/artifacts/ow4p3m6y_WhatsApp%20Image%202026-08-01%20at%2018.47.04.jpeg"
                    alt="ate.teen studio backlit signage lightbox — architecture and interior design studio Kerala"
                    className="h-full w-full object-cover"
                    animate={{ scale: [1, 1.07, 1] }}
                    transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                />
            </motion.div>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-coal via-coal/55 to-coal/20" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-coal/70 via-coal/20 to-transparent" />

            <div className="pointer-events-none relative z-10 mx-auto flex h-full w-full max-w-[1600px] flex-col justify-end px-6 pb-20 sm:px-12 sm:pb-24 lg:px-20">
                <motion.p
                    className="mb-8 flex items-center gap-4 text-[10px] font-medium uppercase tracking-[0.45em] text-ash"
                    initial={{ opacity: 0, y: 16 }}
                    animate={introDone ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.9, delay: d(0), ease: EASE }}
                >
                    <span className="h-px w-10 bg-gold" />
                    Established in 2023 — Kollam
                    <span className="hidden sm:inline">, Kerala</span>
                </motion.p>

                <h1 className="font-display text-[11vw] font-black uppercase leading-[0.95] tracking-tight sm:text-[9.5vw] lg:text-[6.6vw]">
                    <HeroLine delay={d(1)} active={introDone}>
                        Designing
                    </HeroLine>
                    <HeroLine delay={d(2)} active={introDone}>
                        <span className="text-outline">Spaces That</span>
                    </HeroLine>
                    <HeroLine delay={d(3)} active={introDone}>
                        <span className="text-gold">Matter</span>
                    </HeroLine>
                </h1>

                <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
                    <motion.div
                        className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-medium uppercase tracking-[0.2em] text-ash sm:tracking-[0.35em]"
                        initial={{ opacity: 0, y: 20 }}
                        animate={introDone ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.9, delay: d(4), ease: EASE }}
                        data-testid="hero-disciplines"
                    >
                        {["Architecture", "Interiors", "Construction"].map(
                            (w, i) => (
                                <span key={w} className="flex items-center gap-4">
                                    {i > 0 && (
                                        <span className="h-1 w-1 rotate-45 bg-gold" />
                                    )}
                                    {w}
                                </span>
                            ),
                        )}
                    </motion.div>

                    <motion.div
                        className="pointer-events-auto flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={introDone ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.9, delay: d(5), ease: EASE }}
                    >
                        <MagneticButton className="w-full sm:w-auto">
                            <button
                                data-testid="hero-start-project-btn"
                                onClick={() => navigate("/contact")}
                                className="group flex w-full items-center justify-center gap-3 whitespace-nowrap rounded-full bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-coal transition-colors duration-300 hover:bg-white sm:w-auto"
                            >
                                Start Project
                                <ArrowUpRight
                                    size={16}
                                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                />
                            </button>
                        </MagneticButton>
                        <MagneticButton className="w-full sm:w-auto">
                            <button
                                data-testid="hero-view-portfolio-btn"
                                onClick={() => navigate("/portfolio")}
                                className="flex w-full items-center justify-center whitespace-nowrap rounded-full border border-white/20 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-gold hover:text-gold sm:w-auto"
                            >
                                View Portfolio
                            </button>
                        </MagneticButton>
                    </motion.div>
                </div>
            </div>

            <motion.div
                className="absolute bottom-8 right-8 z-10 hidden flex-col items-center gap-3 md:flex"
                initial={{ opacity: 0 }}
                animate={introDone ? { opacity: 1 } : {}}
                transition={{ delay: d(6), duration: 1 }}
            >
                <span className="text-[9px] uppercase tracking-[0.4em] text-ash [writing-mode:vertical-lr]">
                    Scroll
                </span>
                <div className="h-16 w-px overflow-hidden bg-white/10">
                    <div className="animate-scroll-line h-full w-px bg-gold" />
                </div>
                <ArrowDown size={12} className="text-gold" />
            </motion.div>
        </section>
    );
}
